import React, { useEffect, useState, useCallback, useRef, useMemo, memo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./style.css";

const DEFAULT_LOCALE = {
  page: "Page",
  of: "of",
  to: "to",
  more: "More",
  next: "Next",
  last: "Last",
  first: "First",
  previous: "Previous",
  loadingOoo: "Loading...",
  noRowsToShow: "No records found",
};

const ReusableGrid = ({
  rowData,
  columnDefs,
  height = "100%",
  width = "100%",
  defaultColDef: defaultColDefProp,
  gridRef,
  onSelectionChanged,
  onGridReady: onGridReadyProp,
  pagination = true,
  domLayout,
  pageSize = 10,
  emptyMessage = "No records found",
  emptyHint,
  autoRowHeight = false,
  rowHeight: rowHeightProp,
  getRowId,
}) => {
  const [gridApi, setGridApi] = useState(null);
  const internalRef = useRef(null);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      minWidth: 110,
      resizable: true,
      suppressHeaderMenuButton: true,
      ...defaultColDefProp,
    }),
    [defaultColDefProp]
  );

  const onGridReady = useCallback(
    (params) => {
      if (gridRef) {
        gridRef.current = params.api;
      }
      setGridApi(params.api);
      onGridReadyProp?.(params);
    },
    [gridRef, onGridReadyProp]
  );

  useEffect(() => {
    if (gridApi) {
      if (gridApi.setGridOption) {
        gridApi.setGridOption("paginationPageSize", pageSize);
      } else if (gridApi.setPaginationPageSize) {
        gridApi.setPaginationPageSize(pageSize);
      } else if (gridApi.paginationSetPageSize) {
        gridApi.paginationSetPageSize(pageSize);
      }
    }
  }, [pageSize, gridApi]);

  useEffect(() => {
    if (gridApi && autoRowHeight) {
      gridApi.resetRowHeights?.();
    }
  }, [rowData, gridApi, autoRowHeight]);

  const handleSelectionChanged = useCallback(
    (event) => {
      onSelectionChanged?.(event.api.getSelectedRows());
    },
    [onSelectionChanged]
  );

  const hasRows = Array.isArray(rowData) && rowData.length > 0;

  const overlayTemplate = useMemo(
    () =>
      emptyHint
        ? `<div class="ag-grid-empty">
        <div class="ag-grid-empty-icon">📋</div>
        <p class="ag-grid-empty-title">${emptyMessage}</p>
        <p class="ag-grid-empty-hint">${emptyHint}</p>
      </div>`
        : `<div class="ag-grid-empty">
        <div class="ag-grid-empty-icon">📋</div>
        <p class="ag-grid-empty-title">${emptyMessage}</p>
      </div>`,
    [emptyHint, emptyMessage]
  );

  return (
    <div
      className="ag-theme-alpine ag-grid-shell"
      style={{
        height,
        width,
      }}
    >
      <AgGridReact
        ref={internalRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows
        rowHeight={autoRowHeight ? undefined : rowHeightProp ?? 48}
        headerHeight={44}
        rowSelection="multiple"
        suppressRowClickSelection
        domLayout={domLayout || "normal"}
        getRowId={getRowId}
        onGridReady={onGridReady}
        onSelectionChanged={handleSelectionChanged}
        pagination={pagination}
        suppressPaginationPanel={!hasRows}
        paginationAutoPageSize={false}
        paginationPageSize={pageSize}
        localeText={DEFAULT_LOCALE}
        overlayNoRowsTemplate={overlayTemplate}
        suppressCellFocus
        enableCellTextSelection
      />
    </div>
  );
};

export default memo(ReusableGrid);
